package com.klantenstop.web.pages.admin.moderation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.klantenstop.web.components.Dialog
import com.klantenstop.web.components.KsCategoryRow
import com.klantenstop.web.components.KsExternalLink
import com.klantenstop.web.components.H1Heading
import com.klantenstop.web.components.layout.AdminPageLayout
import com.klantenstop.web.components.moderation.ModerationTable
import com.materialkobweb.components.toast.ToastManager
import com.klantenstop.web.data.models.domain.Category
import com.klantenstop.web.data.models.domain.normalizeWebsiteUrl
import com.klantenstop.web.data.models.dto.responses.moderation.CompanySnapshotDto
import com.klantenstop.web.data.models.dto.responses.moderation.ModerationEntityType
import com.klantenstop.web.data.models.dto.responses.moderation.ModerationEntryDetailDto
import com.klantenstop.web.data.models.dto.responses.moderation.ReviewSnapshotDto
import com.klantenstop.web.data.models.dto.responses.moderation.StatusSnapshotDto
import com.klantenstop.web.styles.ErrorTextButtonStyle
import com.klantenstop.web.styles.Typography
import com.materialkobweb.styles.MaterialColorVars
import com.klantenstop.web.styles.PrimaryTextButtonStyle
import com.klantenstop.web.utils.maxLines
import com.materialkobweb.utils.rememberViewModel
import com.materialkobweb.components.widgets.DsCard
import com.materialkobweb.components.widgets.DsSpinner
import com.materialkobweb.components.widgets.FilledButton
import com.materialkobweb.components.widgets.OutlinedButton
import com.materialkobweb.components.widgets.SpinnerSize
import com.materialkobweb.components.widgets.TextButton
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.OverflowWrap
import com.varabyte.kobweb.compose.css.Resize
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.columnGap
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxHeight
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flex
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.overflowWrap
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.resize
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.coroutines.flow.collectLatest
import kotlinx.datetime.Clock
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea

@Page("/admin/moderatie")
@Composable
fun ModerationQueuePage() {
    val ctx = rememberPageContext()
    val entityType = ctx.route.params["type"]?.let { type ->
        ModerationEntityType.entries.find { it.name.equals(type, ignoreCase = true) }
    }

    val viewModel = rememberViewModel(key = "moderation-$entityType") { ModerationQueueViewModel(entityType) }
    val state by viewModel.state.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.effect.collectLatest { effect ->
            when (effect) {
                is ModerationQueueContract.Effect.ShowSuccess -> ToastManager.show(effect.message)
                is ModerationQueueContract.Effect.ShowError -> ToastManager.error(effect.message)
            }
        }
    }

    // Trigger initial load after effect collector is set up
    LaunchedEffect(entityType) {
        viewModel.sendIntent(ModerationQueueContract.Intent.LoadQueue(entityType, page = 1))
    }

    val title = when (entityType) {
        ModerationEntityType.COMPANY -> "Moderatie Wachtrij - Bedrijven"
        ModerationEntityType.STATUS -> "Moderatie Wachtrij - Statussen"
        ModerationEntityType.REVIEW -> "Moderatie Wachtrij - Beoordelingen"
        else -> "Moderatie Wachtrij"
    }

    AdminPageLayout(title = title) {
        ModerationQueueContent(
            state = state,
            title = title,
            sendIntent = viewModel::sendIntent
        )
    }
}

@Composable
private fun ModerationQueueContent(
    state: ModerationQueueContract.State,
    title: String,
    sendIntent: (ModerationQueueContract.Intent) -> Unit
) {
    val breakpoint = rememberBreakpoint()

    Column(
        modifier = Modifier
            .fillMaxWidth(if (breakpoint > Breakpoint.MD) 80.percent else 90.percent)
            .fillMaxHeight()
            .maxWidth(1200.px)
            .padding(leftRight = 24.px, topBottom = 58.px),
        verticalArrangement = Arrangement.spacedBy(24.px)
    ) {
        H1Heading(
            typography = Typography.HeadlineSmall,
            text = title,
            color = MaterialColorVars.Primary.value()
        )

        when {
            state.isLoading -> Box(
                modifier = Modifier.fillMaxWidth(),
                contentAlignment = Alignment.Center
            ) {
                DsSpinner(size = SpinnerSize.Normal)
            }

            state.error != null -> SpanText(
                text = state.error,
                modifier = Modifier.fontSize(14.px).color(MaterialColorVars.Error.value())
            )

            else -> ModerationTable(
                items = state.items,
                expandedRowId = state.expandedRowId,
                onRowClick = { entry ->
                    sendIntent(ModerationQueueContract.Intent.ToggleRow(entryId = entry.id))
                },
                expandedContent = { entry ->
                    ExpandedRowContent(
                        entryDetail = state.entryDetailCache[entry.id],
                        isLoadingSnapshot = entry.id in state.loadingEntryIds,
                        snapshotError = state.snapshotErrorByEntryId[entry.id],
                        onApprove = { sendIntent(ModerationQueueContract.Intent.ApproveEntry(entry.id)) },
                        onReject = { sendIntent(ModerationQueueContract.Intent.ShowRejectDialog(entry.id)) }
                    )
                }
            )
        }

        // Pagination controls
        if (state.total > state.pageSize) {
            PaginationControls(
                currentPage = state.page,
                totalItems = state.total,
                pageSize = state.pageSize,
                onPageChange = { newPage ->
                    sendIntent(ModerationQueueContract.Intent.ChangePage(newPage))
                }
            )
        }

        // Reject dialog
        if (state.rejectDialogEntryId != null) {
            RejectReasonDialog(
                entryId = state.rejectDialogEntryId,
                onSubmit = { entryId, reason ->
                    sendIntent(ModerationQueueContract.Intent.RejectEntry(entryId, reason))
                },
                onDismiss = {
                    sendIntent(ModerationQueueContract.Intent.HideRejectDialog)
                }
            )
        }
    }
}

@Composable
private fun PaginationControls(
    currentPage: Int,
    totalItems: Long,
    pageSize: Int,
    onPageChange: (Int) -> Unit
) {
    val totalPages = ((totalItems + pageSize - 1) / pageSize).toInt()

    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Previous button
        SpanText(
            text = "← Vorige",
            modifier = Modifier
                .padding(8.px)
                .fontSize(14.px)
                .color(if (currentPage > 1) MaterialColorVars.Primary.value() else MaterialColorVars.Outline.value())
                .cursor(if (currentPage > 1) Cursor.Pointer else Cursor.NotAllowed)
                .thenIf(currentPage > 1) {
                    Modifier.onClick { onPageChange(currentPage - 1) }
                }
        )

        SpanText(
            text = "Pagina $currentPage van $totalPages",
            modifier = Modifier
                .padding(leftRight = 24.px)
                .fontSize(14.px)
                .color(MaterialColorVars.OnSurface.value())
        )

        // Next button
        SpanText(
            text = "Volgende →",
            modifier = Modifier
                .padding(8.px)
                .fontSize(14.px)
                .color(if (currentPage < totalPages) MaterialColorVars.Primary.value() else MaterialColorVars.Outline.value())
                .cursor(if (currentPage < totalPages) Cursor.Pointer else Cursor.NotAllowed)
                .thenIf(currentPage < totalPages) {
                    Modifier.onClick { onPageChange(currentPage + 1) }
                }
        )
    }
}


@Composable
private fun ExpandedRowContent(
    entryDetail: ModerationEntryDetailDto?,
    isLoadingSnapshot: Boolean,
    snapshotError: String?,
    onApprove: () -> Unit,
    onReject: () -> Unit
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(16.px)
    ) {
        when {
            isLoadingSnapshot -> Box(
                modifier = Modifier.fillMaxWidth().padding(32.px),
                contentAlignment = Alignment.Center
            ) {
                DsSpinner(size = SpinnerSize.Small)
            }

            snapshotError != null -> SpanText(
                text = "Fout bij laden: $snapshotError",
                modifier = Modifier.fontSize(14.px).color(MaterialColorVars.Error.value())
            )

            entryDetail != null -> {
                // Display snapshot data based on which snapshot field is present
                val companySnapshot = entryDetail.companySnapshot
                val statusSnapshot = entryDetail.statusSnapshot
                val reviewSnapshot = entryDetail.reviewSnapshot

                when {
                    companySnapshot != null -> {
                        CompanySnapshotDisplay(snapshot = companySnapshot)
                    }

                    statusSnapshot != null -> {
                        StatusSnapshotDisplay(snapshot = statusSnapshot)
                    }

                    reviewSnapshot != null -> {
                        ReviewSnapshotDisplay(snapshot = reviewSnapshot)
                    }

                    else -> {
                        SpanText(
                            text = "Geen snapshot gegevens beschikbaar",
                            modifier = Modifier.fontSize(14.px).color(MaterialColorVars.OnSurfaceVariant.value())
                        )
                    }
                }

                // Action buttons
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.End,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    // Reject button
                    TextButton(
                        modifier = Modifier,
                        variant = ErrorTextButtonStyle,
                        onClick = onReject
                    ) {
                        Text("Afkeuren")
                    }

                    // Approve button
                    TextButton(
                        modifier = Modifier,
                        variant = PrimaryTextButtonStyle,
                        onClick = onApprove
                    ) {
                        Text("Goedkeuren")
                    }
                }
            }

            else -> SpanText(
                text = "Geen snapshot beschikbaar",
                modifier = Modifier.fontSize(14.px).color(MaterialColorVars.OnSurfaceVariant.value())
            )
        }
    }
}

@Composable
private fun CompanySnapshotDisplay(snapshot: CompanySnapshotDto) {
    val breakpoint = rememberBreakpoint()
    val padding = if (breakpoint >= Breakpoint.MD) 24.px else 16.px

    DsCard(
        modifier = Modifier.fillMaxWidth(),
        variant = null,
        contentPadding = padding
    ) {
        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(16.px)
        ) {
            // Header: Logo + Title + Verified Badge
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(16.px),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(8.px)
                ) {
                    SpanText(
                        modifier = Typography.TitleLarge.toModifier().maxLines(2).fillMaxWidth().overflowWrap(OverflowWrap.Anywhere),
                        text = snapshot.name
                    )
                }
            }

            // Categories
            if (snapshot.categories.isNotEmpty()) {
                KsCategoryRow(
                    categories = snapshot.categories.map {
                        Category(
                            id = it.id,
                            slug = "",
                            name = it.name,
                            altText = null,
                            highlighted = false,
                            description = "",
                            logo = null,
                            createdAt = Clock.System.now(),
                            updatedAt = Clock.System.now()
                        )
                    },
                    maxVisible = 100
                )
            }

            // Description (truncated)
            if (snapshot.description.isNotBlank()) {
                SpanText(
                    modifier = Modifier
                        .fillMaxWidth()
                        .fontSize(14.px)
                        .lineHeight(20.px)
                        .color(MaterialColorVars.OnSurfaceVariant.value())
                        .overflowWrap(OverflowWrap.BreakWord)
                        .maxLines(2),
                    text = snapshot.description
                )
            }

            // Contact Info
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(8.px)
            ) {
                val contactModifier = Modifier
                    .columnGap(12.px)
                    .fontSize(14.px)
                    .lineHeight(16.px)

                // Always show address
                KsExternalLink(
                    modifier = contactModifier,
                    text = snapshot.fullAddress,
                    href = null,
                    materialIcon = "location_on"
                )

                // Show phone if available
                if (snapshot.phoneNumber.isNotBlank()) {
                    KsExternalLink(
                        modifier = contactModifier,
                        text = snapshot.phoneNumber,
                        href = null,
                        materialIcon = "phone"
                    )
                }

                // Show website if available
                val websiteUrl = snapshot.website.ifEmpty { null }?.let(::normalizeWebsiteUrl)
                if (websiteUrl != null) {
                    KsExternalLink(
                        modifier = contactModifier,
                        text = websiteUrl.href,
                        href = websiteUrl.href,
                        materialIcon = "globe"
                    )
                }
            }
        }
    }
}

@Composable
private fun StatusSnapshotDisplay(snapshot: StatusSnapshotDto) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(12.px)
    ) {
        SpanText(
            text = "Status informatie",
            modifier = Modifier.fontSize(16.px).fontWeight(FontWeight.SemiBold).color(MaterialColorVars.OnSurface.value())
        )

        DetailRow(label = "Tekst", value = snapshot.text)
        DetailRow(label = "Type", value = snapshot.statusType)
        DetailRow(label = "Bedrijf", value = snapshot.companyName ?: snapshot.companyId)
    }
}

@Composable
private fun ReviewSnapshotDisplay(snapshot: ReviewSnapshotDto) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(12.px)
    ) {
        SpanText(
            text = "Beoordeling informatie",
            modifier = Modifier.fontSize(16.px).fontWeight(FontWeight.SemiBold).color(MaterialColorVars.OnSurface.value())
        )

        DetailRow(label = "Rating", value = "${snapshot.rating}/5 sterren")
        DetailRow(label = "Tekst", value = snapshot.text)
        DetailRow(label = "Bedrijf", value = snapshot.companyName ?: snapshot.companyId)
        if (!snapshot.anonymous && snapshot.submittedByUserName != null) {
            DetailRow(label = "Beoordelaar", value = snapshot.submittedByUserName!!)
        }
        DetailRow(label = "Anoniem", value = if (snapshot.anonymous) "Ja" else "Nee")
    }
}

@Composable
private fun DetailRow(label: String, value: String) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.Top
    ) {
        SpanText(
            text = "$label:",
            modifier = Modifier
                .minWidth(120.px)
                .fontSize(14.px)
                .fontWeight(FontWeight.SemiBold)
                .color(MaterialColorVars.OnSurface.value())
        )
        SpanText(
            text = value,
            modifier = Modifier
                .fontSize(14.px)
                .color(MaterialColorVars.OnSurfaceVariant.value())
        )
    }
}

@Composable
private fun RejectReasonDialog(
    entryId: String,
    onSubmit: (String, String) -> Unit,
    onDismiss: () -> Unit
) {
    var reason by remember { mutableStateOf("") }

    Dialog(onHideDialog = onDismiss) {
        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(20.px)
        ) {
            // Title
            SpanText(
                text = "Afkeuren - Reden opgeven",
                modifier = Modifier
                    .fontSize(20.px)
                    .fontWeight(FontWeight.SemiBold)
                    .color(MaterialColorVars.OnSurface.value())
            )

            // Description
            SpanText(
                text = "Geef een reden op waarom deze entry wordt afgekeurd:",
                modifier = Modifier
                    .fontSize(14.px)
                    .color(MaterialColorVars.OnSurfaceVariant.value())
            )

            // TextArea for reason
            TextArea(
                value = reason,
                attrs = Modifier
                    .fillMaxWidth()
                    .height(120.px)
                    .padding(12.px)
                    .fontSize(14.px)
                    .backgroundColor(MaterialColorVars.SurfaceContainerHighest.value())
                    .color(MaterialColorVars.OnSurface.value())
                    .borderRadius(8.px)
                    .resize(Resize.Vertical)
                    .border(1.px, LineStyle.Solid, MaterialColorVars.Outline.value())
                    .fontFamily("inherit")
                    .toAttrs {
                        onInput { reason = it.value }
                        attr("placeholder", "Reden voor afkeuring...")
                    }
            )

            // Action buttons
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(20.px),
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Cancel buttons
                OutlinedButton(
                    modifier = Modifier.flex(1),
                    onClick = onDismiss
                ) {
                    Text("Annuleren")
                }

                // Submit button
                FilledButton(
                    modifier = Modifier.flex(1),
                    enabled = reason.isNotBlank(),
                    onClick = { onSubmit(entryId, reason.trim()) }
                ) {
                    Text("Afkeuren")
                }
            }
        }
    }
}
